import React, { useEffect, useState } from 'react';
import { FiArrowDown, FiArrowRight, FiArrowUp, FiClock, FiExternalLink } from 'react-icons/fi';
import { FantasyNewsItem, listFantasyNews } from '../services/fantasyNews';
import { HomeContentDocument, listHomeContent } from '../services/homeContent';
import { getSeasonStatus, SeasonStatus } from '../services/leagueSettings';
import { listPowerRankings, PowerRanking } from '../services/powerRankings';
import styles from '../styles/home.module.css';

const fallbackRankings: PowerRanking[] = Array.from({ length: 16 }, (_, index) => ({
  rank: index + 1,
  rosterId: String(index + 1),
  teamId: `owner-${index + 1}`,
  owner: `Owner ${String(index + 1).padStart(2, '0')}`,
  projectedPoints: 0,
  avatarUrl: null,
  rankChange: null,
}));

const formatRelativeTime = (publishedAt: string | null) => {
  if (!publishedAt) return 'Recently';

  const elapsedMinutes = Math.round((Date.now() - Date.parse(publishedAt)) / 60000);
  if (!Number.isFinite(elapsedMinutes)) return 'Recently';
  if (elapsedMinutes < 60) return `${Math.max(1, elapsedMinutes)}m ago`;

  const elapsedHours = Math.round(elapsedMinutes / 60);
  if (elapsedHours < 24) return `${elapsedHours}h ago`;

  const elapsedDays = Math.round(elapsedHours / 24);
  return `${elapsedDays}d ago`;
};

const TeamMark = ({ owner, avatarUrl }: { owner: string; avatarUrl: string | null }) => (
  <span className={styles.teamMark} aria-hidden="true">
    {avatarUrl
      ? <img alt="" src={avatarUrl} />
      : owner.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()}
  </span>
);

const Home: React.FunctionComponent = () => {
  const [commissionerNote, setCommissionerNote] = useState(
    'Commissioner updates will live here—a quick place for league reminders, context, and whatever needs everyone’s attention this week.'
  );
  const [leagueNews, setLeagueNews] = useState<HomeContentDocument[]>([]);
  const [rankings, setRankings] = useState<PowerRanking[]>(fallbackRankings);
  const [fantasyNews, setFantasyNews] = useState<FantasyNewsItem[]>([]);
  const [fantasyNewsLoading, setFantasyNewsLoading] = useState(true);
  const [seasonStatus, setSeasonStatus] = useState<SeasonStatus>({
    season: '',
    week: null,
    isInSeason: false,
    phase: 'offseason',
  });

  const seasonPhase = seasonStatus.phase === 'preseason'
    ? `Preseason · Week ${seasonStatus.week}`
    : seasonStatus.phase === 'postseason'
      ? `Postseason · Week ${seasonStatus.week}`
      : seasonStatus.phase === 'regular'
        ? `Week ${seasonStatus.week}`
        : 'Off-season';

  useEffect(() => {
    const loadContent = async () => {
      try {
        const content = await listHomeContent();
        const note = content.find((item) => item.contentType === 'commissioner_note');
        const news = content.filter((item) => item.contentType === 'league_news');
        if (note?.body) setCommissionerNote(note.body);
        setLeagueNews(news);
      } catch (error) {
        console.info('Home content collection is not available yet; using placeholder content.', error);
      }
    };
    loadContent();
  }, []);

  useEffect(() => {
    getSeasonStatus().then(setSeasonStatus).catch((error) => {
      console.info('Sleeper season state is unavailable; showing off-season.', error);
    });
  }, []);

  useEffect(() => {
    listPowerRankings().then((latestRankings) => {
      if (latestRankings.length) setRankings(latestRankings);
    }).catch((error) => {
      console.info('Power rankings are unavailable; showing placeholders.', error);
    });
  }, []);

  useEffect(() => {
    listFantasyNews()
      .then(setFantasyNews)
      .catch((error) => {
        console.info('Fantasy news feeds are temporarily unavailable.', error);
      })
      .finally(() => setFantasyNewsLoading(false));
  }, []);

  return (
    <div className={styles.page}>
      <img alt="" aria-hidden="true" className={styles.watermark} src={`${process.env.PUBLIC_URL}/logo.svg`} />
      <div className={styles.shell}>
        <header className={styles.hero}>
          <h1>League dashboard</h1>
          <p className={styles.intro}>Power rankings, draft grades, season previews - you'll find all of it here.</p>
        </header>

        <section className={styles.seasonLockup} aria-label="Current season status">
          <p className={styles.seasonEyebrow}>Season</p>
          <strong className={styles.seasonYear}>{seasonStatus.season || '—'}</strong>
          <p className={styles.seasonPhase}>{seasonPhase}</p>
        </section>

        <div className={styles.columns}>
          <section className={`${styles.panel} ${styles.rankingsPanel}`} aria-labelledby="power-rankings-heading">
            <div className={styles.sectionHeading}>
              <div>
                <p className={styles.kicker}>WEEKLY</p>
                <h2 id="power-rankings-heading">Power rankings</h2>
              </div>
              <span className={styles.updated}><FiClock aria-hidden="true" /> Wednesdays</span>
            </div>

            <ol className={styles.rankingsList}>
              {rankings.map((team) => (
                <li className={styles.rankingRow} key={team.teamId} title={`${team.projectedPoints.toFixed(2)} projected points`}>
                  <span className={styles.rank}>{String(team.rank).padStart(2, '0')}</span>
                  <TeamMark avatarUrl={team.avatarUrl} owner={team.owner} />
                  <span className={styles.owner}>{team.owner}</span>
                  {team.rankChange && team.rankChange > 0 ? (
                    <span className={`${styles.rankChange} ${styles.rankUp}`} aria-label={`Up ${team.rankChange} places`}>
                      <FiArrowUp aria-hidden="true" /> {team.rankChange}
                    </span>
                  ) : team.rankChange && team.rankChange < 0 ? (
                    <span className={`${styles.rankChange} ${styles.rankDown}`} aria-label={`Down ${Math.abs(team.rankChange)} places`}>
                      <FiArrowDown aria-hidden="true" /> {Math.abs(team.rankChange)}
                    </span>
                  ) : (
                    <span className={styles.rankChange} aria-label="No ranking change">-</span>
                  )}
                </li>
              ))}
            </ol>

          </section>

          <div className={styles.feedColumn}>
            <section className={`${styles.panel} ${styles.commissionerPanel}`} aria-labelledby="commissioner-heading">
              <p className={styles.kicker}>From my home office</p>
              <h2 id="commissioner-heading">Note from the commish</h2>
              <blockquote>
                {commissionerNote}
              </blockquote>
              <p className={styles.signature}>- Conley</p>
            </section>

            <section className={styles.section} aria-labelledby="league-news-heading">
              <div className={styles.sectionHeading}>
                <div>
                  <p className={styles.kicker}>Inside the league</p>
                  <h2 id="league-news-heading">PPA news</h2>
                </div>
              </div>
              <div className={styles.newsGrid}>
                {leagueNews.length ? (
                  leagueNews.map((item) => (
                    <a className={styles.leagueStory} href={item.url || '#league-news-heading'} key={item.$id}>
                      <span>
                        <strong>{item.title}</strong>
                        <small>{item.body}</small>
                      </span>
                      <FiArrowRight aria-hidden="true" />
                    </a>
                  ))
                ) : (
                  <p className={styles.feedMessage}>Nothing for now, but more to come soon.</p>
                )}
              </div>
            </section>

            <section className={styles.section} aria-labelledby="fantasy-news-heading">
              <div className={styles.sectionHeading}>
                <div>
                  <p className={styles.kicker}>Automated feed</p>
                  <h2 id="fantasy-news-heading">Fantasy news</h2>
                </div>
                <span className={styles.liveBadge}><span aria-hidden="true" /> Live feed</span>
              </div>
              <div className={styles.fantasyFeed}>
                {fantasyNews.slice(0, 6).map((story) => (
                  <a
                    className={styles.fantasyStory}
                    href={story.url}
                    key={story.id}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <div>
                      <p className={styles.storyMeta}>{story.source} <span>·</span> {formatRelativeTime(story.publishedAt)}</p>
                      <h3>{story.headline}</h3>
                    </div>
                    <FiExternalLink aria-hidden="true" />
                  </a>
                ))}
                {!fantasyNewsLoading && fantasyNews.length === 0 && (
                  <p className={styles.feedMessage}>Fantasy headlines are temporarily unavailable.</p>
                )}
                {fantasyNewsLoading && <p className={styles.feedMessage}>Loading the latest headlines…</p>}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
